/**
 * Dual-scope type lists (global rows `organization_id IS NULL` + org rows)
 * written through the Hono backend at `/api/v1/dual-scope-types`.
 *
 *   GET    /orgs/:orgId/:table?includeInactive=   → { items }
 *   POST   /orgs/:orgId/:table                    → { item }
 *   PATCH  /orgs/:orgId/:table/:id                → { item }
 *   DELETE /orgs/:orgId/:table/:id                → { deleted: true, id }
 *
 * `DUAL_SCOPE_TABLES` is the one registry of the writable lists: the route
 * resolves `:table` against it (unknown → 404) and parses the body with the
 * table's own `.strict()` create/update schema (the router cannot use a
 * static `validateJson`, the schema depends on the path param). Every column
 * named here is a REAL column of that table — verified against
 * `20251223111223_remote_schema.sql` (`asset_types` / `business_types` /
 * `project_types`), `20260420144822_create_materials_lca_taxonomies.sql`
 * (the four LCA tables), `20260928170000_interactions.sql`
 * (`interaction_types` / `interaction_roles`) and `types/database.ts`. The enum values mirror the
 * LCA migration's CHECK constraints exactly.
 *
 * `connection_types` is deliberately NOT in the registry — it keeps its own
 * member-writable routes under `/api/v1/people` (docs/DUAL_SCOPE_TYPES.md).
 *
 * Scope rules (enforced by the route + RLS, not by these schemas):
 *   - `scope: 'global'` → super admins only (`super_admin_required`);
 *   - `scope: 'org'`    → org admins only, no super-admin bypass (`admin_required`).
 * PATCH / DELETE follow the stored row's scope. PATCH never writes
 * `organization_id` or `code` (both absent from the update schemas).
 */
import { z } from "zod";
// ---------------------------------------------------------------------------
// Code slug
// ---------------------------------------------------------------------------
/**
 * The `code` format a create may supply: lowercase alphanumerics and `_`,
 * starting with a letter or digit. Branch B's vocab tables CHECK the same
 * regex. (Legacy global rows such as `EQUIPMENT` predate it; `code` is
 * immutable, so the rule only ever applies to new rows.)
 */
export const DUAL_SCOPE_CODE_REGEX = /^[a-z0-9][a-z0-9_]*$/;
/**
 * The `code` a new vocabulary row gets from its name: lowercase, every run of
 * `[^a-z0-9]` → `_`, trimmed of `_`; prefixed `t_` when it does not start with
 * a letter. An empty slug (a name of only punctuation) becomes `type`. The
 * result always matches both `DUAL_SCOPE_CODE_REGEX` and
 * `connection_types_code_format` (`^[a-z][a-z0-9_]*$`) — the same slug rule as
 * the `20260924112446_connection_types_dual_scope` backfill. Collisions are
 * the route's job (`_2` … `_5`).
 */
export function vocabCodeFromName(name) {
    const slug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    if (slug === "")
        return "type";
    return /^[a-z]/.test(slug) ? slug : `t_${slug}`;
}
// ---------------------------------------------------------------------------
// Field helpers
// ---------------------------------------------------------------------------
/** `''` / whitespace-only → `null`; anything else passes through untouched. */
const blankToNull = (value) => typeof value === "string" && value.trim() === "" ? null : value;
/** Optional, nullable free text; `''` clears the column. */
const optionalText = (max) => z.preprocess(blankToNull, z.string().trim().max(max).nullable()).optional();
/** Optional value from a fixed set, or `null` (a nullable CHECK column). */
const optionalNullableEnum = (values) => z.preprocess(blankToNull, z.enum(values).nullable()).optional();
const nameField = z.string().trim().min(1).max(255);
// ---------------------------------------------------------------------------
// CHECK-constraint vocabularies (20260420144822, verbatim)
// ---------------------------------------------------------------------------
/** `procurement_types_circularity_tier_check` (NOT NULL, default `neutral`). */
export const PROCUREMENT_CIRCULARITY_TIERS = ["circular", "extended_life", "neutral", "excluded"];
/** `procurement_types_ghg_category_check` (or NULL). */
export const PROCUREMENT_GHG_CATEGORIES = ["scope_3_cat_1"];
/** `end_of_life_plans_waste_hierarchy_tier_check` (NOT NULL, default `disposal`). */
export const END_OF_LIFE_WASTE_HIERARCHY_TIERS = [
    "prevention",
    "reuse",
    "recycling",
    "recovery",
    "disposal",
];
/** `end_of_life_plans_ghg_category_check` (or NULL). */
export const END_OF_LIFE_GHG_CATEGORIES = ["scope_3_cat_5", "scope_3_cat_12"];
/** `waste_treatment_methods_treatment_code_standard_check` (or NULL). */
export const WASTE_TREATMENT_CODE_STANDARDS = ["eu_wfd", "us_epa_rcra", "basel", "uk_wfr"];
// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------
/** Where a new row lives. `global` = `organization_id NULL` (super admins only). */
export const DUAL_SCOPE_SCOPES = ["org", "global"];
/**
 * Columns every dual-scope list shares, minus `name`/`code` (create-vs-update differ).
 * `sort_order` / `is_active` / `is_default` / `metadata` are nullable columns (with
 * defaults) and `DualScopeTypeWire` carries them as `| null`, so they accept `null`
 * — a stored row picked with `pickDualScopeFields` must round-trip without a 400.
 */
const coreOptionalShape = {
    description: optionalText(2000),
    sort_order: z.number().int().min(-1_000_000).max(1_000_000).nullable().optional(),
    is_active: z.boolean().nullable().optional(),
    is_default: z.boolean().nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
};
const colorPairShape = {
    color_bg: optionalText(100),
    color_text: optionalText(100),
};
/** Per-table extra columns; every key is optional (the DB has a default or allows NULL). */
const EXTRA_SHAPES = {
    asset_types: {
        icon: optionalText(100),
        color: optionalText(100),
    },
    business_types: {},
    project_types: {},
    procurement_types: {
        ...colorPairShape,
        circularity_tier: z.enum(PROCUREMENT_CIRCULARITY_TIERS).optional(),
        ghg_category: optionalNullableEnum(PROCUREMENT_GHG_CATEGORIES),
    },
    recycled_content_types: {
        ...colorPairShape,
        // numeric(3,2): refuse a precision the column would silently round (0.125 → 0.13).
        circular_content_value: z.number().min(0).max(1).multipleOf(0.01).optional(),
    },
    end_of_life_plans: {
        ...colorPairShape,
        waste_hierarchy_tier: z.enum(END_OF_LIFE_WASTE_HIERARCHY_TIERS).optional(),
        ghg_category: optionalNullableEnum(END_OF_LIFE_GHG_CATEGORIES),
        requires_waste_method: z.boolean().optional(),
    },
    waste_treatment_methods: {
        ...colorPairShape,
        treatment_code: optionalText(50),
        treatment_code_standard: optionalNullableEnum(WASTE_TREATMENT_CODE_STANDARDS),
        is_compliant: z.boolean().optional(),
    },
    // Branch B (20260928170000_interactions.sql): the two interaction vocabularies — core columns only.
    interaction_types: {},
    interaction_roles: {},
};
/** The writable lists, in display order (Branch B appended the two interaction vocabularies). */
export const DUAL_SCOPE_TABLE_NAMES = [
    "business_types",
    "project_types",
    "asset_types",
    "procurement_types",
    "recycled_content_types",
    "end_of_life_plans",
    "waste_treatment_methods",
    "interaction_types",
    "interaction_roles",
];
const AT_LEAST_ONE_FIELD = "At least one field must be provided";
function buildCreateSchema(extras) {
    return z
        .object({
        scope: z.enum(DUAL_SCOPE_SCOPES),
        name: nameField,
        code: z.string().trim().min(1).max(100).regex(DUAL_SCOPE_CODE_REGEX).optional(),
        ...coreOptionalShape,
        ...extras,
    })
        .strict();
}
function buildUpdateSchema(extras) {
    return z
        .object({
        name: nameField.optional(),
        ...coreOptionalShape,
        ...extras,
    })
        .strict()
        .refine((value) => Object.keys(value).length > 0, { message: AT_LEAST_ONE_FIELD });
}
/** The column keys of a table's writable set (create minus `scope`), in shape order. */
function writableKeys(extras) {
    return ["name", "code", ...Object.keys(coreOptionalShape), ...Object.keys(extras)];
}
// Return type left inferred: each registry entry is checked against
// `DualScopeTableSpec` at its concrete `K` (a generic `K` cannot be resolved here).
function spec(table, codeKey) {
    const extras = EXTRA_SHAPES[table];
    const pickFields = writableKeys(extras);
    return {
        create: buildCreateSchema(extras),
        update: buildUpdateSchema(extras),
        codeKey,
        pickFields,
        updateFields: pickFields.filter((key) => key !== "code"),
    };
}
/**
 * The registry. `codeKey`s: `remote_schema.sql:9659/9669/9734`,
 * `20260420144822:43/131/222/313` (all confirmed against the live catalog),
 * and `20260928170000_interactions.sql` for the two interaction vocabularies
 * (`UNIQUE NULLS NOT DISTINCT (organization_id, code)`; their `code` CHECK is
 * `DUAL_SCOPE_CODE_REGEX`).
 */
export const DUAL_SCOPE_TABLES = {
    business_types: spec("business_types", "unique_business_type_code_per_org"),
    project_types: spec("project_types", "unique_project_type_code_per_org"),
    asset_types: spec("asset_types", "unique_asset_type_code_per_org"),
    procurement_types: spec("procurement_types", "unique_procurement_type_code_per_org"),
    recycled_content_types: spec("recycled_content_types", "unique_recycled_content_type_code_per_org"),
    end_of_life_plans: spec("end_of_life_plans", "unique_end_of_life_plan_code_per_org"),
    waste_treatment_methods: spec("waste_treatment_methods", "unique_waste_treatment_method_code_per_org"),
    interaction_types: spec("interaction_types", "interaction_types_org_code_key"),
    interaction_roles: spec("interaction_roles", "interaction_roles_org_code_key"),
};
/** Narrow a `:table` path param to a registry key (unknown → the route 404s). */
export function isDualScopeTableName(value) {
    return Object.prototype.hasOwnProperty.call(DUAL_SCOPE_TABLES, value);
}
/**
 * Pick only the keys in `fields` that are present (not `undefined`) on `row`.
 * Accepts any object (e.g. a `DualScopeTypeWire` grid row) — pass
 * `updateFields` for a PATCH body so a stored `code` is never sent.
 */
export function pickDualScopeFields(row, fields) {
    const src = row;
    const out = {};
    for (const key of fields) {
        if (src[key] !== undefined)
            out[key] = src[key];
    }
    return out;
}
// ---------------------------------------------------------------------------
// Coded error envelopes (the web reads `err.envelope?.code`)
// ---------------------------------------------------------------------------
export const DUAL_SCOPE_TYPE_ERROR_CODES = {
    /** 409 — DELETE refused by a NO ACTION FK (23503): the row is referenced. Untick Active instead. */
    inUse: "type_in_use",
    /** 409 — a supplied `code` clashes on the table's `codeKey` (or the `_2…_5` retry ran out). */
    codeTaken: "type_code_taken",
    /** 403 — an org-row write by a caller whose org role is not `admin` (no super-admin bypass). */
    adminRequired: "admin_required",
    /** 403 — a global-row write by a caller who is not a super admin. */
    superAdminRequired: "super_admin_required",
};
//# sourceMappingURL=dualScopeTypes.js.map